/**
 * Offset, meter and markers (W1-10a; DESIGN.md §5.2 "First beat", rpe-format F§3.4).
 *
 * Offset: Phira plays beat 0 at music time META.offset / 1000 + info.yml `offset` (+ the player's own device offset).
 * META.offset is an i32 of milliseconds, so the chart offset is split: integer ms into `timing.offsetMs`, the
 * sub-millisecond remainder (seconds) into `pkg.infoOffsetSec`. An imported info.yml offset of a millisecond or more
 * (Phira's per-chart adjustment) stays separate until the user folds it in: `setOffset` sets the chart offset and
 * keeps the whole-millisecond part of that adjustment; `foldInfoOffset` moves the whole milliseconds of it into
 * META.offset, keeping only a sub-millisecond remainder in info.yml.
 *
 * Meter: the marks are stored sorted, and there is always one at beat 0 (bar lines, bar seeking and metronome
 * accents count bars from it); a list without one gets the default 4-beat mark there, and the transaction notes it.
 */

import { Defaults } from '../model/defaults';
import type { Marker, MarkerId, MeterMark } from '../model/types';
import { Beats } from '../time/beat';
import { isFiniteNumber, isI32, markerOf, noNegZero, type Patch, type Tx } from './structureKit';
import type { TempoCommands } from './types';

/** Whole milliseconds and the sub-millisecond remainder (seconds) of an offset in ms. */
export function splitOffsetMs(ms: number): { readonly offsetMs: number; readonly remainderSec: number } {
  const offsetMs = noNegZero(Math.round(ms));
  return { offsetMs, remainderSec: cleanSec((ms - offsetMs) / 1000) };
}

/** Rounds float noise off a seconds value (to the nanosecond). */
function cleanSec(s: number): number {
  return noNegZero(Math.round(s * 1e9) / 1e9);
}

/**
 * Whole-millisecond part of an info.yml offset (the per-chart adjustment), in ms: the nearest whole millisecond, so
 * a sub-millisecond remainder of either sign (|r| < 0.5 ms) never changes it.
 */
export function infoAdjustmentMs(infoOffsetSec: number): number {
  return noNegZero(Math.round(Math.round(infoOffsetSec * 1e6) / 1e3));
}

const setOffset: TempoCommands['setOffset'] = (tx: Tx, { ms }) => {
  if (!isFiniteNumber(ms)) tx.fail({ key: 'cmd.timing.offsetInvalid' });
  const { offsetMs, remainderSec } = splitOffsetMs(ms);
  if (!isI32(offsetMs)) tx.fail({ key: 'cmd.timing.offsetInvalid' });
  const adjustment = infoAdjustmentMs(tx.doc.pkg.infoOffsetSec) / 1000;
  tx.updateSingleton('timing', { offsetMs });
  tx.updateSingleton('pkg', { infoOffsetSec: cleanSec(adjustment + remainderSec) });
};

const foldInfoOffset: TempoCommands['foldInfoOffset'] = (tx: Tx) => {
  const info = tx.doc.pkg.infoOffsetSec;
  const whole = infoAdjustmentMs(info);
  if (whole === 0) return;
  const offsetMs = tx.doc.timing.offsetMs + whole;
  if (!isI32(offsetMs)) tx.fail({ key: 'cmd.timing.offsetInvalid' });
  tx.updateSingleton('timing', { offsetMs });
  tx.updateSingleton('pkg', { infoOffsetSec: cleanSec(info - whole / 1000) });
  tx.note({ key: 'cmd.timing.folded', params: { ms: whole } });
};

const setTimingConfirmed: TempoCommands['setTimingConfirmed'] = (tx: Tx, { confirmed }) => {
  tx.updateSingleton('timing', { confirmed });
};

const setMeter: TempoCommands['setMeter'] = (tx: Tx, { marks }) => {
  const sorted: MeterMark[] = [...marks].sort((a, b) => Beats.cmp(a.t, b.t));
  for (let i = 0; i < sorted.length; i++) {
    const m = sorted[i] as MeterMark;
    const prev = i > 0 ? sorted[i - 1] : undefined;
    const ok =
      Beats.isNormal(m.t) &&
      Beats.cmp(m.t, Beats.ZERO) >= 0 &&
      isFiniteNumber(m.beatsPerBar) &&
      m.beatsPerBar > 0 &&
      (prev === undefined || Beats.lt(prev.t, m.t));
    if (!ok) tx.fail({ key: 'cmd.timing.meterInvalid' });
  }
  const first = sorted[0];
  if (first === undefined || !Beats.eq(first.t, Beats.ZERO)) {
    const start = defaultMeterStart();
    sorted.unshift(start);
    tx.note({ key: 'cmd.timing.meterStartAdded', params: { beatsPerBar: start.beatsPerBar } });
  }
  tx.updateSingleton('timing', { meter: sorted.map((m) => ({ t: m.t, beatsPerBar: m.beatsPerBar })) });
};

/** The mark a meter gets at beat 0 when none is given there: the first mark of the default meter (4 beats per bar). */
export function defaultMeterStart(): MeterMark {
  const m = Defaults.timing().meter[0];
  return { t: Beats.ZERO, beatsPerBar: m?.beatsPerBar ?? 4 };
}

function checkMarkerBeat(tx: Tx, t: Marker['t']): void {
  if (!Beats.isNormal(t)) tx.fail({ key: 'cmd.badBeat' });
  if (Beats.cmp(t, Beats.ZERO) < 0) tx.fail({ key: 'cmd.markers.beforeStart' });
}

const addMarker: TempoCommands['addMarker'] = (tx: Tx, { t, name }) => {
  checkMarkerBeat(tx, t);
  const id: MarkerId = tx.newId('markers');
  tx.insert('markers', { id, t, name: name.trim(), color: null });
  tx.setSelection({ markers: [id] });
  tx.setFocus({ beat: t });
  return id;
};

const patchMarker: TempoCommands['patchMarker'] = (tx: Tx, { id, name, t, color }) => {
  markerOf(tx, id);
  const patch: Patch<Marker> = {};
  if (name !== undefined) patch.name = name.trim();
  if (t !== undefined) {
    checkMarkerBeat(tx, t);
    patch.t = t;
  }
  if (color !== undefined) patch.color = color;
  if (Object.keys(patch).length > 0) tx.update('markers', id, patch);
};

const deleteMarker: TempoCommands['deleteMarker'] = (tx: Tx, { id }) => {
  markerOf(tx, id);
  tx.remove('markers', id);
  tx.setSelection({ markers: [] });
};

export const timingCommands = {
  setOffset,
  foldInfoOffset,
  setTimingConfirmed,
  setMeter,
  addMarker,
  patchMarker,
  deleteMarker,
} as const;
